import { useState, useEffect, useCallback } from "react";

/**
 * Reactive media query hook.
 * Returns true when the CSS media query matches, updates on resize.
 *
 * Usage:
 *   const isMobile = useMediaQuery("(max-width: 768px)");
 *   const prefersReducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
 */
export function useMediaQuery(query: string): boolean {
  const getMatches = useCallback(
    () => window.matchMedia(query).matches,
    [query],
  );

  const [matches, setMatches] = useState<boolean>(() => {
    // Guard for SSR
    if (typeof window === "undefined") return false;
    return getMatches();
  });

  useEffect(() => {
    const mql = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);

    // Set initial value (in case it changed between render and effect)
    setMatches(mql.matches);

    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, [query, getMatches]);

  return matches;
}

/** Convenience: true when viewport width <= 768px */
export function useIsMobile(): boolean {
  return useMediaQuery("(max-width: 768px)");
}

/** Convenience: true when viewport is in landscape orientation */
export function useIsLandscape(): boolean {
  return useMediaQuery("(orientation: landscape)");
}
